import React, { useEffect, useRef, useState } from 'react';
import { Box, FormHelperText, InputBase } from '@mui/material';

// Geführte Eingabe für deutsche Kfz-Kennzeichen, visuell als Schild
// dargestellt: blaues EU-Band links, dann drei Segmente
// (Unterscheidungszeichen 1–3 Buchstaben, Erkennungsbuchstaben 1–2,
// Erkennungsnummer 1–4 Ziffern mit optionalem E/H-Suffix).
//
// Wichtig: Der emittierte Wert ist das kanonische Speicherformat der
// Bestandsdaten — Segmente mit EINFACHEM Leerzeichen verbunden, z. B.
// "PB TI 727". Die Fahrer-App matcht Kennzeichen als exakten String;
// ein anderes Format würde bestehende Queries im Feld brechen.

// Zerlegt einen vorhandenen Kennzeichen-String in die drei Segmente.
// Toleriert Bindestriche/Mehrfach-Leerzeichen aus Altbeständen. Nicht
// parsebare Werte landen best-effort im ersten Segment, damit beim
// Bearbeiten nichts verloren geht.
export const parsePlate = (value) => {
  const raw = String(value || '').toUpperCase().trim();
  if (!raw) return { city: '', letters: '', numbers: '' };
  const match = raw
    .replace(/[-\s]+/g, ' ')
    .match(/^([A-ZÄÖÜ]{1,3}) ?([A-Z]{1,2}) ?(\d{1,4}[EH]?)$/);
  if (match) return { city: match[1], letters: match[2], numbers: match[3] };
  const tokens = raw.replace(/[-\s]+/g, ' ').split(' ');
  return {
    city: tokens[0] || '',
    letters: tokens[1] || '',
    numbers: tokens.slice(2).join('') || '',
  };
};

export const joinPlate = ({ city, letters, numbers }) => (
  [city, letters, numbers].filter(Boolean).join(' ')
);

// Vollständigkeits-Check fürs Submit (nicht während der Eingabe erzwingen).
export const isValidGermanPlate = (value) => (
  /^[A-ZÄÖÜ]{1,3} [A-Z]{1,2} \d{1,4}[EH]?$/.test(String(value || '').trim())
);

// Vergleichsschlüssel für Duplikat-Prüfungen: Groß, Sonderzeichen-tolerant.
export const normalizePlateForCompare = (value) => (
  String(value || '').toUpperCase().replace(/[-\s]+/g, ' ').trim()
);

const SEGMENTS = [
  { key: 'city', maxLength: 3, charset: /[^A-ZÄÖÜ]/g, placeholder: 'PB', width: 64 },
  { key: 'letters', maxLength: 2, charset: /[^A-Z]/g, placeholder: 'TI', width: 48 },
  { key: 'numbers', maxLength: 5, charset: /[^0-9EH]/g, placeholder: '727', width: 84 },
];

const segmentInputSx = {
  '& input': {
    textAlign: 'center',
    fontFamily: '"DIN Condensed", "Arial Narrow", sans-serif',
    fontWeight: 700,
    fontSize: '1.6rem',
    letterSpacing: '0.12em',
    color: '#111',
    padding: '2px 0',
    textTransform: 'uppercase',
    '&::placeholder': { color: '#bbb', opacity: 1 },
  },
};

const LicensePlateInput = ({ value, onChange, error, helperText, disabled }) => {
  const [parts, setParts] = useState(() => parsePlate(value));
  const refs = { city: useRef(null), letters: useRef(null), numbers: useRef(null) };

  // Externe Wertänderung (z. B. Laden im Edit-Modus) übernehmen — aber nur,
  // wenn sie nicht von der eigenen Eingabe stammt, sonst springt der Cursor.
  useEffect(() => {
    setParts(prev => (joinPlate(prev) === String(value || '').toUpperCase().trim()
      ? prev
      : parsePlate(value)));
  }, [value]);

  const handleSegmentChange = (segment) => (e) => {
    const spec = SEGMENTS.find(s => s.key === segment);
    const cleaned = e.target.value
      .toUpperCase()
      .replace(spec.charset, '')
      .slice(0, spec.maxLength);
    const next = { ...parts, [segment]: cleaned };
    setParts(next);
    onChange(joinPlate(next));

    // Auto-Sprung ins nächste Segment, sobald das aktuelle voll ist.
    if (cleaned.length >= spec.maxLength) {
      const index = SEGMENTS.findIndex(s => s.key === segment);
      const nextKey = SEGMENTS[index + 1]?.key;
      if (nextKey) refs[nextKey].current?.focus();
    }
  };

  const handleKeyDown = (segment) => (e) => {
    if (e.key === 'Backspace' && !parts[segment]) {
      const index = SEGMENTS.findIndex(s => s.key === segment);
      const prevKey = SEGMENTS[index - 1]?.key;
      if (prevKey) refs[prevKey].current?.focus();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'stretch',
          border: '2px solid #111',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: error ? (theme) => `0 0 0 2px ${theme.palette.error.main}` : 'none',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {/* EU-Band */}
        <Box
          sx={{
            width: 30,
            backgroundColor: '#003399',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            py: 0.5,
          }}
        >
          <Box sx={{ fontSize: '0.6rem', lineHeight: 1, color: '#FFCC00' }}>★</Box>
          <Box sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1 }}>D</Box>
        </Box>

        {SEGMENTS.map((spec, index) => (
          <React.Fragment key={spec.key}>
            <InputBase
              inputRef={refs[spec.key]}
              value={parts[spec.key]}
              onChange={handleSegmentChange(spec.key)}
              onKeyDown={handleKeyDown(spec.key)}
              placeholder={spec.placeholder}
              disabled={disabled}
              inputProps={{
                maxLength: spec.maxLength,
                'aria-label': spec.key === 'city'
                  ? 'Ortskennung'
                  : spec.key === 'letters' ? 'Buchstaben' : 'Nummer',
                autoCapitalize: 'characters',
                autoComplete: 'off',
                spellCheck: false,
              }}
              sx={{ ...segmentInputSx, width: spec.width, px: 1 }}
            />
            {index === 0 && (
              // Angedeutete Prüf-/Zulassungsplaketten zwischen Ortskennung
              // und Erkennungszeichen — wie auf dem echten Schild.
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5, px: 0.25 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#e8b23d' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4d6b9e' }} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
      {helperText && (
        <FormHelperText error={!!error} sx={{ ml: 0.5 }}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

export default LicensePlateInput;
